<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Hero con imagen de fondo -->
    <ImageBackgroundSection
      :image-url="heroImage"
      :loading-image="heroImageLoading"
      alt="Historias"
      :title="heroTitle"
      :subtitle="heroSubtitle"
    />

    <!-- Listado de historias -->
    <section class="px-6 lg:px-0 py-12 lg:pt-24 lg:pb-0 max-w-7xl mx-auto">
      <div class="section-header">
        <div class="section-header__icon">
          <IconFbochas name="mensaje" :size="32" color="#fff" />
        </div>
        <span class="section-header__label">Historias</span>
      </div>

      <!-- Grid de cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <RevealOnScroll
          v-for="(historia, index) in historias"
          :key="historia.slug"
          as="article"
          :delay="(index % 3) * 100"
          class="listing-motion-card card-fbochas overflow-hidden flex flex-col active:scale-[0.98]"
        >
          <router-link
            :to="`/historias/${historia.slug}`"
            class="flex flex-col h-full"
          >
            <!-- Imagen de la persona -->
            <div class="h-[250px] overflow-hidden bg-bochas-bg">
              <img
                v-if="historia.imagen"
                :src="historia.imagen"
                :alt="historia.nombre"
                class="listing-motion-image w-full h-full object-cover"
                loading="lazy"
              />
              <div
                v-else
                class="flex h-full w-full items-center justify-center bg-degradado-bochas text-5xl font-bold text-white"
              >
                {{ initials(historia.nombre) }}
              </div>
            </div>

            <!-- Contenido -->
            <div class="p-5 sm:p-6 flex-1 flex flex-col">
              <p
                class="text-sm sm:text-lg text-white font-normal italic mb-4 line-clamp-3"
              >
                "{{ historia.quote }}"
              </p>
              <div class="mt-auto">
                <h3
                  class="text-lg sm:text-2xl font-bold text-white mb-1 leading-tight"
                >
                  {{ historia.nombre }}
                </h3>
                <p class="text-xs sm:text-sm text-white mb-3">
                  {{ historia.club }} — {{ historia.años }} años jugando
                </p>
                <span class="text-bochas-rojo text-base font-semibold">
                  Leer historia completa
                </span>
              </div>
            </div>
          </router-link>
        </RevealOnScroll>
      </div>

      <!-- Paginación -->
      <Paginación
        :current-page="currentPage"
        :total-items="historias.length"
        :per-page="perPage"
        @change="onPageChange"
      />

      <!-- CTA: ¿Tenés una historia para compartir? -->
      <RevealOnScroll>
        <CtaCard
          title="¿Tenés una historia para compartir?"
          description="Queremos conocer tu experiencia en las bochas. Cada historia suma a nuestra comunidad."
        >
          <button
            class="motion-action bg-bochas-azul hover:bg-bochas-accent/80 text-white font-semibold px-6 py-3 rounded-full w-full sm:w-auto text-center"
          >
            Enviar mi historia
          </button>
        </CtaCard>
      </RevealOnScroll>

      <!-- Volver -->
      <div class="py-24">
        <VolverButton />
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useListingState } from "@/composables/useListingState";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import Paginación from "@/components/Common/Paginacion.vue";
import CtaCard from "@/components/Common/CtaCard.vue";
import VolverButton from "@/components/Common/VolverButton.vue";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";
import RevealOnScroll from "@/components/Animations/RevealOnScroll.vue";
import { obtenerSiteMediaMap } from "@/services/siteMedia/siteMediaService";

// Pool de imágenes random para el hero
const heroImage = ref("");
const heroImageLoading = ref(true);
const heroTitle = ref("");
const heroSubtitle = ref("");

onMounted(async () => {
  try {
    const media = await obtenerSiteMediaMap();
    const hero = media["historias.hero"];
    heroImage.value = hero?.imageUrl || "";
    heroTitle.value = hero?.title || "Historias";
    heroSubtitle.value =
      hero?.subtitle ||
      "Voces de quienes viven y sienten las bochas cordobesas";
  } catch (_) {
    heroImage.value = "";
    heroTitle.value = "Historias";
    heroSubtitle.value =
      "Voces de quienes viven y sienten las bochas cordobesas";
  } finally {
    heroImageLoading.value = false;
  }
});

// Datos hardcodeados de historias (reemplazar por endpoint del backend)
const historias = ref([
  {
    slug: "osvaldo-bianchi",
    nombre: "Osvaldo 'El Maestro' Bianchi",
    club: "Club Atlético Belgrano",
    años: 50,
    quote:
      "Las bochas me dieron mis mejores amigos y la disciplina que usé en toda mi vida.",
    imagen:
      "",
  },
  {
    slug: "nelida-garcia",
    nombre: "Nélida 'Pochi' García",
    club: "Club Social y Deportivo Brinkmann",
    años: 32,
    quote:
      "Empecé cuándo casi no había mujeres en las canchas. Hoy ver a las chicas competir es mi mayor orgullo.",
    imagen: "",
  },
  {
    slug: "facundo-capdevila",
    nombre: "Facundo Capdevila",
    club: "Asociación Juaréz Celman",
    años: 12,
    quote:
      "Herede las bochas de mi abuelo. Es un deporte de precisión, pero sobre todo de mucha cabeza.",
    imagen:
      "",
  },
  {
    slug: "marta-sierras",
    nombre: "Marta de las Sierras",
    club: "Club Alianza de Jesús María",
    años: 45,
    quote:
      "La cancha es el patio de mi casa. No me imagino un domingo sin el ruido de las bochas chocando.",
    imagen:
      "",
  },
  {
    slug: "ricardo-rossi",
    nombre: "Ricardo 'Turco' Rossi",
    club: "Sociedad Italiana de Morteros",
    años: 55,
    quote:
      "Para arrimar bien hay que tener el pulso de un cirujano y la paciencia de un santo.",
    imagen:
      "",
  },
  {
    slug: "sofia-luna",
    nombre: "Sofía Luna",
    club: "Club San Jorge de Arroyito",
    años: 8,
    quote:
      "Es el deporte más federal que tenemos. En cada pueblo hay una cancha y una historia esperando ser contada.",
    imagen:
      "",
  },
]);

// Paginación
const perPage = 6;
const { currentPage } = useListingState("historias", { currentPage: 1 });

function initials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function onPageChange(page) {
  currentPage.value = page;
}
</script>
